import { Component, HostBinding, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppRoutes } from '@app';
import {
  CardTypes,
  formatCostForInternationalInvoice,
  handleFirstDayStorage,
  handleReturnCargoDate,
  todayEqualOrGreaterThan,
  Unsubscriber,
} from '@shared';
import { LocalStorageService } from 'ngx-webstorage';
import { BehaviorSubject, finalize, map, merge, Observable, of, switchMap, take } from 'rxjs';
import { PaymentCostsInfo } from '@models';
import { InvoiceServiceRequest } from '../../../models/invoice/invoice-service-request.model';
import { Invoice } from '@models';
import { AlertToastService, AuthService, GoogleAnalyticService } from '@services';
import { FirebaseService } from '../../../services/firebase/firebase.service';
import { InvoiceApiService } from '../../../services/invoices/invoice-api.service';
import { InvoiceService } from '../../../services/invoices/invoice.service';
import { MasterpassService } from '../../../services/masterpass.service';
import { TrackDeliveryService } from '../../../services/track-delivery.service';
import { PayInvoiceType } from '../../other/payment-strategy';
import { ShowInvoiceComponent } from '../show-invoice/show-invoice.component';
import { ShowInvoiceService } from '../show-invoice/show-invoice.service';
import * as moment from 'moment';
import {
  allowPayAsThirdPerson,
} from '../../../components/dashboard/components/header/header-search/search-result/components/tracked-invoices/tracked-invoices.utils';
import { BecomePayerDialogComponent } from '../become-payer-dialog';
import { ChangeEwSuccessPopupComponent } from '../change-ew';
import { becomePayerDialogConfig } from '../become-payer-dialog';
import { ChangeEwSaveResponseData } from '../change-ew/models';

@Component({
  selector: 'np-show-invoice-tracked',
  templateUrl: './show-invoice-tracked.component.html',
  styleUrls: [
    './show-invoice-tracked.component.scss',
    '../../../../styles/invoice-show.scss',
  ],
})
export class ShowInvoiceTrackedComponent extends Unsubscriber implements OnInit, OnDestroy {
  protected readonly formatCostForInternationalInvoice = formatCostForInternationalInvoice;

  @HostBinding('class')
  @Input() classes = 'modal-flex';
  @Input() type: PayInvoiceType;
  @Input() inputInvoice: any;
  @Input() contactPhone: string;
  @Input() recipientCounterpartyType: string;
  @Input() backMoney: number;
  @Input() paymentControl: number;

  invoice: Invoice | any;
  getStatusDocumentsInvoice: Invoice;

  newInvoice: Invoice;

  formattedInvoiceNumber: string;

  pending: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  pending$ = this.pending.asObservable();
  loading$: Observable<any> = merge(this.pending$);

  isShowFull = false;

  redirectRequest: InvoiceServiceRequest;

  returnRequest: InvoiceServiceRequest;

  trackingDocuments = [];

  paymentCostsInfo: PaymentCostsInfo;

  public payerNameTranslations = {
    Sender: 'Відправник',
    Recipient: 'Отримувач',
    ThirdPerson: 'Третя особа',
  };
  public paymentMethodTranslations = {
    Cash: 'Готівка',
    NonCash: 'Безготівка',
  };

  currentStateIsArchived = false;
  corporate = false;
  paymentSum: string;

  shouldReload = false;
  showBecomePayerButton: boolean = false;

  constructor(
    private router: Router,
    private invoiceService: InvoiceService,
    private invoicesService: InvoiceApiService,
    private alertToastService: AlertToastService,
    public dialogRef: MatDialogRef<ShowInvoiceTrackedComponent>,
    public dialog: MatDialog,
    public firebaseService: FirebaseService,
    protected storage: LocalStorageService,
    public authService: AuthService,
    protected googleAnalyticsService: GoogleAnalyticService,
    protected masterpassService: MasterpassService,
    private trackDeliveryService: TrackDeliveryService,
    private sis: ShowInvoiceService,
  ) {
    super();
  }

  get RecipientAddressDescription(): string {
    return this.inputInvoice.trackedAddress.recipientAddress;
  }

  get SenderAddressDescription(): string {
    return this.inputInvoice.trackedAddress.senderAddress;
  }

  get statusClass(): string {
    return this.inputInvoice && this.inputInvoice.state
      ? `invoice-state-${this.inputInvoice.state.id}`
      : '';
  }

  get user() {
    return this.authService.user;
  }

  get invoiceCost(): string {
    if (this.inputInvoice?.isInternational) {
      return formatCostForInternationalInvoice(this.inputInvoice?.deliveryPrice + this.inputInvoice?.StoragePrice, this.inputInvoice.DocumentCostCurrencyCode);
    }

    return (this.inputInvoice?.deliveryPrice + this.inputInvoice?.StoragePrice) + ' грн';
  }

  get hideIsPaid(): boolean {
    return this.inputInvoice?.InternationalDeliveryType
      && this.inputInvoice.InternationalDeliveryType === 'Export'
      && this.inputInvoice.PayerType === 'Recipient';
  }

  ngOnInit(): void {
    this.initInvoice();
  }

  private initInvoice(): void {
    this.backMoney = this.inputInvoice?.backMoney;
    this.paymentControl = this.inputInvoice?.paymentControl;
    this.corporate = !!this.user.contacts;
    this.currentStateIsArchived = this.router.url.includes(
      AppRoutes.invoices_archived,
    );

    this.dialog.afterAllClosed
      .pipe(take(1))
      .subscribe(() => this.dialog.closeAll());
    this.getInvoice();
    this.initTrackingDocuments();
  }

  removeFromTrackingDocuments(): void {
    this.sis.closeParams = { event: 'removeFromTrackingDocs', docNumber: this.inputInvoice?.docNumber };
    this.dialogRef.close();
  }

  isPresentedInTrackingDocuments(docNumber: string): boolean {
    return this.trackingDocuments.indexOf(docNumber) > -1;
  }

  switchShowFull(): void {
    this.isShowFull = !this.isShowFull;
  }

  goToNewInvoice(docNumber, event): void {
    event.preventDefault();

    this.showNewInvoice(docNumber);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (event.keyCode === 27) {
      this.dialog.closeAll();
    }
  }

  checkIfPaid(paymentCostsInfo: PaymentCostsInfo): boolean {
    if (!paymentCostsInfo) {
      return true;
    }

    return !(paymentCostsInfo.Services > 0 ||
      paymentCostsInfo.AfterPayment > 0 ||
      paymentCostsInfo.MoneyTransfer > 0 ||
      paymentCostsInfo.InternationalDelivery > 0 ||
      paymentCostsInfo.Commission > 0);
  }

  showNewInvoice(docNUmber: string): void {
    this.openPopup(docNUmber);
  }

  openPopup(
    documentNumber: string = null,
  ): void {
    this.setPending(true);

    this.subscriptions = this.getInvoiceByNumber(documentNumber)
      .subscribe((invoice: Invoice) => {

        const invoiceToOpen = invoice ?? this.newInvoice;
        const isTrackedDocument = !invoice;

        const modalConfig = {
          panelClass: 'no-padding',
        };

        this.closePopups();

        let dialogRef: MatDialogRef<ShowInvoiceTrackedComponent | ShowInvoiceComponent>;
        if (isTrackedDocument) {
          dialogRef = this.dialog.open(ShowInvoiceTrackedComponent, modalConfig);
        } else {
          dialogRef = this.dialog.open(ShowInvoiceComponent, modalConfig);
        }

        const invoiceType = invoiceToOpen.TypeOfDocument === 'Incoming' || invoiceToOpen.TypeOfDocument === 'in' ? 'in' : 'out';

        dialogRef.componentInstance.type = invoiceType;
        dialogRef.componentInstance.inputInvoice = invoiceToOpen;

      }, (errors) => {
        if (errors[0]) {
          this.alertToastService.pushApiErrors(errors);
        }
      });
  }

  get createdDate(): Date {
    return moment(this.inputInvoice.DateTime || this.inputInvoice.dateTime, 'DD-MM-YYYY HH:mm:ss').toDate();
  }

  closePopups(): void {
    this.dialog.closeAll();
  }

  handleInvoiceMutations(invoice: Invoice): void {
    this.inputInvoice = handleFirstDayStorage(invoice);
    this.inputInvoice = handleReturnCargoDate(this.inputInvoice);
  }

  private initTrackingDocuments(): void {
    if (this.storage.isStorageAvailable()) {
      const trackingDocumentsArray =
        this.trackDeliveryService.getTrackingDocuments();
      if (!!trackingDocumentsArray) {
        this.trackingDocuments = trackingDocumentsArray;
      }
    }
  }

  private setPending(value: boolean): void {
    this.pending.next(value);
  }

  private getInvoice(): void {
    this.setPending(true);

    this.formattedInvoiceNumber = this.invoiceService.makeFormatedInvoiceNumber(this.inputInvoice.docNumber);

    this.handleInvoiceMutations(this.inputInvoice);
    this.handleInvoiceState(this.inputInvoice);

    this.invoiceService
      .track(
        this.inputInvoice.docNumber,
        this.inputInvoice?.PhoneSender ?? undefined,
      )
      .pipe(
        switchMap((invoice) => {
          this.inputInvoice = invoice;
          this.getStatusDocumentsInvoice = invoice;

          this.handleInvoiceMutations(this.inputInvoice);
          this.handleInvoiceState(this.inputInvoice);
          this.processServiceRequests();
          this.setShowBecomePayerButton();

          this.formattedInvoiceNumber = this.invoiceService.makeFormatedInvoiceNumber(this.inputInvoice.docNumber);

          return this.getPaymentInfo();
        }),
        finalize(() => this.setPending(false)),
        take(1),
      )
      .subscribe((paymentCostsInfo) => this.paymentCostsInfo = paymentCostsInfo);
  }

  private getPaymentInfo(): Observable<any> {
    return this.masterpassService.getPaymentInfo(
      this.inputInvoice.docNumber,
      this.type === 'in'
        ? this.inputInvoice.recipient &&
        (this.inputInvoice.recipient.phone as string)
        : this.authService.user.phone ||
        (this.inputInvoice.sender ? this.inputInvoice.sender.phone : ''),
    );
  }

  private handleInvoiceState(invoice: Invoice): void {
    const isTodayGreaterThanFirstDayStorage = (i: Invoice): boolean => {
      if (!i) {
        return false;
      }
      try {
        const dateFirstDayStorage = new Date(i.dateFirstDayStorage);
        return todayEqualOrGreaterThan(dateFirstDayStorage);
      } catch (e) {
        return false;
      }
    };

    if (
      !invoice.dateReturnCargo &&
      !(
        invoice &&
        invoice.statusCode &&
        !!invoice.dateFirstDayStorage &&
        (Number(invoice.statusCode) === 7 || Number(invoice.statusCode) === 8) && isTodayGreaterThanFirstDayStorage(invoice)
      )
    ) {
      this.inputInvoice.state.name = invoice.state.name;
    }
  }

  private processServiceRequests(): void {
    if (!this.inputInvoice.serviceRequests) {
      return;
    }

    this.inputInvoice.serviceRequests.forEach((request) => {
      if (request.serviceName === 'Redirecting') {
        this.redirectRequest = request;
      }

      if (request.serviceName === 'CargoReturn') {
        this.returnRequest = request;
      }
    });

    if (this.redirectRequest || this.returnRequest) {
      this.invoiceService
        .track((this.redirectRequest || this.returnRequest).newInvoiceNumber)
        .pipe(take(1))
        .subscribe((i) => this.newInvoice = i);
    }
  }

  private getInvoiceByNumber(invoiceNumber: string): Observable<Invoice | null> {
    return this.invoicesService.searchInvoices(invoiceNumber, { page: 1, limit: 5 })
      .pipe(finalize(() => this.setPending(false)),
        map(({ invoices, totalCount }) => {
          if (!totalCount) {
            return null;
          }

          return invoices.map(innerInvoices => innerInvoices.map(handleFirstDayStorage)[0])[0];
        }),
      );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  close(): void {
    this.dialogRef.close();
  }

  becomePayer(): void {
    const dialogRef: MatDialogRef<BecomePayerDialogComponent> = this.dialog.open(BecomePayerDialogComponent, {
      data: {
        invoice: this.inputInvoice,
      },
      ...becomePayerDialogConfig,
    });

    dialogRef.afterClosed()
      .pipe(
        switchMap((data: ChangeEwSaveResponseData) => {
        if (data) {
          const successDialog = this.dialog.open(ChangeEwSuccessPopupComponent, {
            data: {
              Number: data.Number,
              MessageText: 'Зміна платника буде внесена до ЕН',
            },
            panelClass: 'no-padding',
          });

          return successDialog.afterClosed().pipe(map(() => true));
        }

        return of(false);
      }))
      .subscribe((success: boolean) => {
        if (success) {
          this.shouldReload = true;
          this.close();
        }
      });
  }

  private setShowBecomePayerButton(): void {
    this.showBecomePayerButton = allowPayAsThirdPerson(this.inputInvoice)
      && this.authService.user.loyaltyCardType !== CardTypes.ROZNIZA;
  }
}
